import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'
import { toast } from 'sonner'
import { useLanguage } from '../context/LanguageContext'
import { cn } from '../lib/utils'
import {
  Calculator, TrendingUp, PiggyBank, Percent, Calendar,
  Save, Trash2, Play, ChevronRight, Loader2, DollarSign,
  BarChart3, Table, FolderOpen
} from 'lucide-react'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

// Chart colors
const CHART_COLORS = {
  principal: '#3b82f6',
  contributions: '#10b981',
  interest: '#f59e0b',
  total: '#8b5cf6'
}

export default function CompoundInterestPage() {
  const { t, locale } = useLanguage()
  
  // Form state
  const [form, setForm] = useState({
    principal: 10000,
    contribution: 1000,
    contribution_frequency: 'monthly',
    annual_rate: 10,
    compounding_frequency: 'monthly',
    years: 10,
    contribution_timing: 'end',
    inflation_rate: ''
  })
  
  // Results state
  const [result, setResult] = useState(null)
  const [calculating, setCalculating] = useState(false)
  
  // Scenarios state
  const [scenarios, setScenarios] = useState([])
  const [loadingScenarios, setLoadingScenarios] = useState(true)
  const [showSaveDialog, setShowSaveDialog] = useState(false)
  const [scenarioName, setScenarioName] = useState('')
  const [savingScenario, setSavingScenario] = useState(false)
  
  // Active tab
  const [activeTab, setActiveTab] = useState('calculator')

  // Fetch saved scenarios
  const fetchScenarios = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/compound/scenarios`)
      setScenarios(response.data)
    } catch (error) {
      console.error('Error fetching scenarios:', error)
    } finally {
      setLoadingScenarios(false)
    }
  }, [])

  useEffect(() => {
    fetchScenarios()
  }, [fetchScenarios])

  // Calculate
  const handleCalculate = async () => {
    setCalculating(true)
    try {
      const payload = {
        ...form,
        inflation_rate: form.inflation_rate ? parseFloat(form.inflation_rate) : null
      }
      const response = await axios.post(`${API}/compound/calc`, payload)
      setResult(response.data)
    } catch (error) {
      console.error('Error calculating:', error)
      toast.error(t('common.error'))
    } finally {
      setCalculating(false)
    }
  }

  // Save scenario
  const handleSaveScenario = async () => {
    if (!scenarioName.trim()) {
      toast.error(locale === 'es-MX' ? 'Ingresa un nombre' : 'Enter a name')
      return
    }
    
    setSavingScenario(true)
    try {
      await axios.post(`${API}/compound/scenarios`, {
        name: scenarioName,
        ...form,
        inflation_rate: form.inflation_rate ? parseFloat(form.inflation_rate) : null
      })
      toast.success(t('compound.scenarioSaved'))
      setShowSaveDialog(false)
      setScenarioName('')
      fetchScenarios()
    } catch (error) {
      toast.error(t('common.error'))
    } finally {
      setSavingScenario(false)
    }
  }

  // Load scenario
  const loadScenario = (scenario) => {
    setForm({
      principal: scenario.principal,
      contribution: scenario.contribution,
      contribution_frequency: scenario.contribution_frequency,
      annual_rate: scenario.annual_rate,
      compounding_frequency: scenario.compounding_frequency,
      years: scenario.years,
      contribution_timing: scenario.contribution_timing,
      inflation_rate: scenario.inflation_rate || ''
    })
    setActiveTab('calculator')
    toast.success(locale === 'es-MX' ? 'Escenario cargado' : 'Scenario loaded')
  }

  // Delete scenario
  const deleteScenario = async (id) => {
    try {
      await axios.delete(`${API}/compound/scenarios/${id}`)
      toast.success(t('compound.scenarioDeleted'))
      fetchScenarios()
    } catch (error) {
      toast.error(t('common.error'))
    }
  }

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat(locale === 'es-MX' ? 'es-MX' : 'en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value)
  }

  // Simple line chart using CSS
  const ProjectionChart = ({ data }) => {
    if (!data || data.length === 0) return null
    
    const maxValue = Math.max(...data.map(d => d.ending_balance))
    const chartData = data.filter((_, i) => i % Math.ceil(data.length / 20) === 0 || i === data.length - 1)
    
    return (
      <div className="space-y-2">
        <div className="flex items-end gap-1 h-40">
          {chartData.map((point, idx) => {
            const height = (point.ending_balance / maxValue) * 100
            const principalRatio = form.principal / point.ending_balance
            const contribRatio = (result?.total_contributed - form.principal) / point.ending_balance
            
            return (
              <div key={idx} className="flex-1 flex flex-col justify-end h-full">
                <div 
                  className="w-full rounded-t transition-all relative group"
                  style={{ 
                    height: `${height}%`,
                    background: `linear-gradient(to top, ${CHART_COLORS.principal} ${principalRatio * 100}%, ${CHART_COLORS.contributions} ${(principalRatio + contribRatio) * 100}%, ${CHART_COLORS.interest})`
                  }}
                >
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-background border rounded px-2 py-1 text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                    {formatCurrency(point.ending_balance)}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{t('compound.year')} 1</span>
          <span>{t('compound.year')} {form.years}</span>
        </div>
        <div className="flex gap-4 justify-center text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: CHART_COLORS.principal }} />
            <span>{t('compound.principal')}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: CHART_COLORS.contributions }} />
            <span>{t('compound.contribution')}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: CHART_COLORS.interest }} />
            <span>{t('compound.interest')}</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-600 to-teal-700 text-white px-4 py-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Calculator className="w-6 h-6" />
          {t('compound.title')}
        </h1>
        <p className="text-emerald-200 mt-1">
          {locale === 'es-MX' ? 'Simula el crecimiento de tus inversiones' : 'Simulate your investment growth'}
        </p>
      </div>

      <div className="px-4 -mt-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="calculator" className="gap-2">
              <Calculator className="w-4 h-4" />
              {t('compound.calculator')}
            </TabsTrigger>
            <TabsTrigger value="scenarios" className="gap-2">
              <FolderOpen className="w-4 h-4" />
              {t('compound.scenarios')}
            </TabsTrigger>
          </TabsList>

          {/* Calculator Tab */}
          <TabsContent value="calculator" className="space-y-4">
            <Card>
              <CardContent className="pt-4 space-y-4">
                {/* Principal */}
                <div className="space-y-2">
                  <Label>{t('compound.principal')}</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="number"
                      value={form.principal}
                      onChange={(e) => setForm(prev => ({ ...prev, principal: parseFloat(e.target.value) || 0 }))}
                      className="pl-9"
                      data-testid="principal-input"
                    />
                  </div>
                </div>

                {/* Contribution */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>{t('compound.contribution')}</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="number"
                        value={form.contribution}
                        onChange={(e) => setForm(prev => ({ ...prev, contribution: parseFloat(e.target.value) || 0 }))}
                        className="pl-9"
                        data-testid="contribution-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('compound.contributionFrequency')}</Label>
                    <Select 
                      value={form.contribution_frequency} 
                      onValueChange={(v) => setForm(prev => ({ ...prev, contribution_frequency: v }))}
                    >
                      <SelectTrigger data-testid="contrib-freq-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">{t('compound.weekly')}</SelectItem>
                        <SelectItem value="biweekly">{t('compound.biweekly')}</SelectItem>
                        <SelectItem value="monthly">{t('compound.monthly')}</SelectItem>
                        <SelectItem value="yearly">{t('compound.yearly')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Rate with presets */}
                <div className="space-y-2">
                  <Label>{t('compound.annualRate')}</Label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="number"
                        step="0.1"
                        value={form.annual_rate}
                        onChange={(e) => setForm(prev => ({ ...prev, annual_rate: parseFloat(e.target.value) || 0 }))}
                        className="pl-9"
                        data-testid="rate-input"
                      />
                    </div>
                    <div className="flex gap-1">
                      {[6, 9, 12].map(rate => (
                        <Button
                          key={rate}
                          variant={form.annual_rate === rate ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setForm(prev => ({ ...prev, annual_rate: rate }))}
                          className="px-3"
                        >
                          {rate}%
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Years and Compounding */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>{t('compound.years')}</Label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="number"
                        value={form.years}
                        onChange={(e) => setForm(prev => ({ ...prev, years: parseInt(e.target.value) || 1 }))}
                        className="pl-9"
                        min="1"
                        max="50"
                        data-testid="years-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('compound.compoundingFrequency')}</Label>
                    <Select 
                      value={form.compounding_frequency} 
                      onValueChange={(v) => setForm(prev => ({ ...prev, compounding_frequency: v }))}
                    >
                      <SelectTrigger data-testid="compound-freq-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">{t('compound.daily')}</SelectItem>
                        <SelectItem value="monthly">{t('compound.monthly')}</SelectItem>
                        <SelectItem value="yearly">{t('compound.yearly')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Timing */}
                <div className="space-y-2">
                  <Label>{t('compound.contributionTiming')}</Label>
                  <Select 
                    value={form.contribution_timing} 
                    onValueChange={(v) => setForm(prev => ({ ...prev, contribution_timing: v }))}
                  >
                    <SelectTrigger data-testid="timing-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="end">{t('compound.atEnd')}</SelectItem>
                      <SelectItem value="begin">{t('compound.atBegin')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Inflation (optional) */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    {t('compound.inflationRate')}
                    <span className="text-xs text-muted-foreground">({t('compound.optional')})</span>
                  </Label>
                  <div className="relative">
                    <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="number"
                      step="0.1"
                      value={form.inflation_rate}
                      onChange={(e) => setForm(prev => ({ ...prev, inflation_rate: e.target.value }))}
                      className="pl-9"
                      placeholder="3.5"
                      data-testid="inflation-input"
                    />
                  </div>
                </div>

                {/* Calculate Button */}
                <Button 
                  onClick={handleCalculate} 
                  disabled={calculating}
                  className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700"
                  size="lg"
                  data-testid="calculate-btn"
                >
                  {calculating ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Play className="w-5 h-5 mr-2" />
                  )}
                  {t('compound.calculate')}
                </Button>
              </CardContent>
            </Card>

            {/* Results */}
            {result && (
              <>
                {/* KPIs */}
                <div className="grid grid-cols-1 gap-3">
                  <Card className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border-emerald-500/30">
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">{t('compound.futureValue')}</p>
                          <p className="text-3xl font-bold text-emerald-600" data-testid="fv-result">
                            {formatCurrency(result.fv)}
                          </p>
                        </div>
                        <TrendingUp className="w-10 h-10 text-emerald-500/50" />
                      </div>
                    </CardContent>
                  </Card>

                  <div className="grid grid-cols-2 gap-3">
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-xs text-muted-foreground">{t('compound.totalContributed')}</p>
                        <p className="text-xl font-bold text-blue-600" data-testid="contributed-result">
                          {formatCurrency(result.total_contributed)}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-xs text-muted-foreground">{t('compound.totalInterest')}</p>
                        <p className="text-xl font-bold text-amber-600" data-testid="interest-result">
                          {formatCurrency(result.total_interest)}
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Chart */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <BarChart3 className="w-4 h-4" />
                      {t('compound.timeline')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ProjectionChart data={result.timeline} />
                  </CardContent>
                </Card>

                {/* Yearly Summary Table */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Table className="w-4 h-4" />
                      {t('compound.yearlySummary')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2 px-1">{t('compound.year')}</th>
                            <th className="text-right py-2 px-1">{t('compound.balance')}</th>
                            <th className="text-right py-2 px-1">{t('compound.interest')}</th>
                            {form.inflation_rate && (
                              <th className="text-right py-2 px-1">{t('compound.realValue')}</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {result.yearly_summary.map((row) => (
                            <tr key={row.year} className="border-b border-dashed">
                              <td className="py-2 px-1 font-medium">{row.year}</td>
                              <td className="text-right py-2 px-1">{formatCurrency(row.ending_balance)}</td>
                              <td className="text-right py-2 px-1 text-amber-600">{formatCurrency(row.interest)}</td>
                              {form.inflation_rate && (
                                <td className="text-right py-2 px-1 text-muted-foreground">
                                  {row.real_ending_balance ? formatCurrency(row.real_ending_balance) : '-'}
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* Save Button */}
                <Button
                  onClick={() => setShowSaveDialog(true)}
                  variant="outline"
                  className="w-full rounded-full"
                  data-testid="save-scenario-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {t('compound.saveScenario')}
                </Button>
              </>
            )}
          </TabsContent>

          {/* Scenarios Tab */}
          <TabsContent value="scenarios" className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{t('compound.savedScenarios')}</CardTitle>
              </CardHeader>
              <CardContent>
                {loadingScenarios ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                  </div>
                ) : scenarios.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">
                    {t('compound.noScenarios')}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {scenarios.map((scenario) => (
                      <div
                        key={scenario.id}
                        className="flex items-center justify-between p-3 rounded-xl bg-secondary/50"
                        data-testid={`scenario-${scenario.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{scenario.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {formatCurrency(scenario.principal)} • {scenario.annual_rate}% • {scenario.years} {locale === 'es-MX' ? 'años' : 'years'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => loadScenario(scenario)}
                            data-testid={`load-scenario-${scenario.id}`}
                          >
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => deleteScenario(scenario.id)}
                            data-testid={`delete-scenario-${scenario.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Save Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('compound.saveScenario')}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>{t('compound.scenarioName')}</Label>
            <Input
              value={scenarioName}
              onChange={(e) => setScenarioName(e.target.value)}
              placeholder={locale === 'es-MX' ? 'Ej: Retiro 15 años' : 'E.g: 15-year retirement'}
              className="mt-2"
              data-testid="scenario-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSaveScenario} disabled={savingScenario} data-testid="confirm-save-btn">
              {savingScenario ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
